import { useMemo } from "react";
import { graphqlClient } from "@/api/graphql";
import { useMyConsultsPageQuery } from "@/graphql/generated";
import { ONE_HOUR } from "@/app/_helpers/constants";
import {
  customSort,
  checkForAndAppendMedicareAndMedical,
  backfillMissingConsultIds,
} from "./_utils";
export const myConsultsQueryKey = "myConsultsPage";

export const useGetMyConsultsPageQuery = (
  clinician_ids: number[] | null,
  appointment_type: string[] = [
    "consult",
    "psychiatry_assessment",
    "therapy_assessment",
  ],
) => {
  const isEnabled = (clinician_ids || []).length > 0;

  // The generated hook keys on variables (`['myConsultsPage', variables]`), so
  // these bounds must be stable across renders or the page refetches forever.
  const { thirtyDaysAgo, fourteenDaysFromNow, oneYearAgo, endOfToday } =
    useMemo(() => {
      const now = new Date();

      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      thirtyDaysAgo.setHours(0, 0, 0, 0);

      const fourteenDaysFromNow = new Date(now);
      fourteenDaysFromNow.setDate(now.getDate() + 14);
      fourteenDaysFromNow.setHours(0, 0, 0, 0);

      const oneYearAgo = new Date(now);
      oneYearAgo.setFullYear(now.getFullYear() - 1);
      oneYearAgo.setHours(0, 0, 0, 0);

      // Floor to end-of-day so pastUpperDate does not change every millisecond.
      const endOfToday = new Date(now);
      endOfToday.setHours(23, 59, 59, 999);

      return { thirtyDaysAgo, fourteenDaysFromNow, oneYearAgo, endOfToday };
      // Stable for the component mount. Flooring above already makes these
      // day-stable if this memo ever recomputes.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

  const { data, isLoading, refetch, isError } = useMyConsultsPageQuery(
    graphqlClient,
    {
      clinician_ids: clinician_ids,
      lowerDate: thirtyDaysAgo,
      upperDate: fourteenDaysFromNow,
      pastLowerDate: oneYearAgo,
      pastUpperDate: endOfToday,
      appointmentType: appointment_type,
    },
    {
      queryKey: [myConsultsQueryKey],
      select: (data) => {
        data.my_matches = backfillMissingConsultIds(data.my_matches);
        data.my_matches.sort(customSort);
        data.my_matches = checkForAndAppendMedicareAndMedical(data.my_matches);
        return data;
      },
      enabled: isEnabled,
      staleTime: ONE_HOUR,
    },
  );

  return { data, isLoading, refetch, isError };
};
